<template>
  <main class="w-full">
    <!-- Header -->
    <header class="mb-2 py-2 px-6 bg-white sticky top-0">
      <h1 class="text-3xl font-bold text-gray-800">Utilisateurs</h1>
      <p class="mb-2 text-gray-600">Gérez les comptes clients</p>
      <!-- Actions Bar -->
      <div class="flex flex-col sm:flex-row gap-4 mb-6">
        <div class="relative flex-1">
          <icon name="Search" class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            placeholder="Rechercher par nom ou email..."
            class="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            v-model="searchTerm"
            @input="handleSearch"
          />
        </div>
        <select
          class="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          v-model="filterRole"
          @change="handleFilter"
        >
          <option value="">Tous les rôles</option>
          <option value="1">Administrateur</option>
          <option value="0">Client</option>
        </select>
        <select
          class="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          v-model="filterStatus"
          @change="handleFilter"
        >
          <option value="">Tous les statuts</option>
          <option value="1">Actif</option>
          <option value="0">Inactif</option>
          <option value="2">Bloqué</option>
        </select>
      </div>
    </header>
    
    <div class="p-6 lg:p-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span class="ml-3 text-gray-600">Chargement des utilisateurs...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div class="flex items-start">
          <icon name="AlertCircle" class="h-5 w-5 text-red-400 mr-2 mt-0.5" />
          <div>
            <p class="text-red-700 font-medium">Erreur lors du chargement des utilisateurs</p>
            <p class="text-red-600 text-sm mt-1">{{ error }}</p>
            <button 
              @click="fetchUsers"
              class="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
            >
              <icon name="RefreshCw" class="h-4 w-4 inline mr-1" />
              Réessayer
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!filteredUsers.length" class="text-center py-12">
        <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <icon name="Users" :size="32" class="text-gray-400" />
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">
          {{ searchTerm || filterRole || filterStatus ? 'Aucun résultat' : 'Aucun utilisateur' }}
        </h3>
        <p class="text-gray-600 mb-6 max-w-md mx-auto">
          {{ searchTerm || filterRole || filterStatus 
            ? 'Aucun utilisateur ne correspond à vos critères de recherche.' 
            : 'Aucun utilisateur n\'est inscrit pour le moment.' 
          }}
        </p>
        <button 
          v-if="searchTerm || filterRole || filterStatus"
          @click="clearFilters"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition inline-flex items-center"
        >
          <icon name="X" class="h-4 w-4 mr-2" />
          Réinitialiser les filtres
        </button>
        <button 
          v-else
          @click="fetchUsers"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition inline-flex items-center"
        >
          <icon name="RefreshCw" class="h-4 w-4 mr-2" />
          Recharger
        </button>
      </div>

      <!-- Users Table -->
      <div v-else class="bg-white rounded-xl min-h-[70vh] shadow overflow-hidden">
        <!-- Stats Summary -->
        <div class="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-6">
              <div class="flex items-center space-x-2">
                <span class="text-sm text-gray-600">
                  {{ filteredUsers.length }} utilisateur{{ filteredUsers.length > 1 ? 's' : '' }}
                </span>
              </div>
              <div class="flex items-center space-x-4">
                <div class="flex items-center space-x-2">
                  <span class="w-3 h-3 rounded-full bg-blue-100 border border-blue-300"></span>
                  <span class="text-sm text-gray-600">Admins: {{ roleCounts[1] || 0 }}</span>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="w-3 h-3 rounded-full bg-green-100 border border-green-300"></span>
                  <span class="text-sm text-gray-600">Clients: {{ roleCounts[0] || 0 }}</span>
                </div>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <button 
                @click="exportUsers"
                class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition flex items-center"
                :disabled="exporting"
              >
                <icon name="Download" class="h-4 w-4 mr-1" />
                {{ exporting ? 'Export...' : 'Exporter' }}
              </button>
              <button 
                @click="fetchUsers"
                class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition flex items-center"
                :disabled="refreshing"
              >
                <icon name="RefreshCw" class="h-4 w-4 mr-1" :class="{ 'animate-spin': refreshing }" />
                {{ refreshing ? 'Actualisation...' : 'Actualiser' }}
              </button>
            </div>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Utilisateur</th>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Contact</th>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Inscription</th>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Commandes</th>
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Total dépensé</th>
                <!-- <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Rôle</th> -->
                <!-- <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Statut</th> -->
                <th class="text-left p-4 text-sm font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="user in filteredUsers"
                :key="user.id"
                class="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                :class="{ 'bg-yellow-50': user.status == 0, 'bg-red-50': user.status == 2 }"
              >
                <td class="p-4">
                  <div class="flex items-center space-x-3">
                    <div class="w-10 h-10 rounded-full flex items-center justify-center"
                         :class="user.role == 1 ? 'bg-purple-100' : 'bg-blue-100'">
                      <span class="font-semibold" :class="user.role == 1 ? 'text-purple-600' : 'text-blue-600'">
                        {{ getUserInitials(user) }}
                      </span>
                    </div>
                    <div>
                      <span class="font-medium text-gray-900">{{ user.name }}</span>
                      <p class="text-xs text-gray-500">
                        ID: {{ user.id }}
                      </p>
                    </div>
                  </div>
                </td>
                <td class="p-4">
                  <div class="text-sm">
                    <p class="text-gray-900">{{ user.email }}</p>
                    <p class="text-gray-500">{{ user.phone || 'Non renseigné' }}</p>
                  </div>
                </td>
                <td class="p-4 text-sm text-gray-700 whitespace-nowrap">
                  {{ formatDate(user.created_at) }}
                  <div class="text-xs text-gray-500">{{ formatTime(user.created_at) }}</div>
                </td>
                <td class="p-4 text-sm font-medium text-gray-900">
                  {{ user.orders_count || user.orders?.length || 0 }}
                </td>
                <td class="p-4 text-sm font-medium text-gray-900">
                  {{ formatPrice(user.total_spent || 0) }}
                </td>
                <td class="p-4">
                  <span :class="`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`">
                    {{ getRoleLabel(user.role) }}
                  </span>
                </td>
                <!-- <td class="p-4">
                  <div class="flex flex-col gap-1">
                    <span :class="`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(user.status)}`">
                      {{ getStatusLabel(user.status) }}
                    </span>
                    <select
                      :value="user.status"
                      @change="updateUserStatus(user.id, $event.target.value)"
                      :disabled="updatingStatus === user.id"
                      class="text-xs px-2 py-1 border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 w-full"
                    >
                      <option value="1">Actif</option>
                      <option value="0">Inactif</option>
                      <option value="2">Bloqué</option>
                    </select>
                  </div>
                </td> -->
                <td class="p-4">
                  <div class="flex items-center space-x-2">
                    <button
                      @click="selectUser(user)"
                      class="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition"
                      title="Voir les détails"
                    >
                      <icon name="Eye" :size="16" class="mr-1" />
                      <span class="hidden sm:inline">Détails</span>
                    </button>
                    
                    <!-- Bouton pour changer le rôle -->
                    <!-- <select
                      v-if="user.id !== currentUserId"
                      :value="user.role"
                      @change="updateUserRole(user.id, $event.target.value)"
                      :disabled="updatingRole === user.id"
                      class="text-xs px-2 py-1 border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                      title="Changer le rôle"
                    >
                      <option value="0">Client</option>
                      <option value="1">Admin</option>
                    </select> -->
                    
                    <!-- Bouton pour envoyer un message -->
                    <button
                      v-if="user.email"
                      @click="sendMessage(user)"
                      class="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                      title="Envoyer un message"
                    >
                      <icon name="Mail" :size="16" />
                    </button>
                    
                    <!-- Bouton pour supprimer -->
                    <!-- <button
                      v-if="user.id !== currentUserId"
                      @click="confirmDelete(user)"
                      class="p-1.5 border border-gray-300 rounded-lg hover:bg-red-50 hover:text-red-600 transition"
                      title="Supprimer"
                    >
                      <icon name="Trash2" :size="16" />
                    </button> -->
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- User Detail Modal -->
    <UserDetailModal
      v-if="selectedUser"
      :user="selectedUser"
      @close="selectedUser = null"
      @update-status="handleStatusUpdate"
      @update-role="handleRoleUpdate"
    />

    <!-- Send Message Modal -->
    <div
      v-if="messageUser"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="messageUser = null"
    >
      <div class="bg-white rounded-xl shadow-lg w-full max-w-md">
        <div class="p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-800">Envoyer un message</h3>
            <button
              @click="messageUser = null"
              class="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <icon name="X" :size="20" class="text-gray-500" />
            </button>
          </div>

          <div class="mb-4">
            <p class="text-sm text-gray-600 mb-1">À:</p>
            <p class="font-medium">{{ messageUser.name }} ({{ messageUser.email }})</p>
          </div>

          <form @submit.prevent="sendUserMessage" class="space-y-4">
            <div>
              <label for="subject" class="block text-sm font-medium text-gray-700 mb-2">
                Sujet
              </label>
              <input
                id="subject"
                v-model="messageForm.subject"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Sujet du message"
              />
            </div>

            <div>
              <label for="message" class="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                id="message"
                v-model="messageForm.message"
                required
                rows="5"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Votre message..."
              ></textarea>
            </div>

            <div class="flex space-x-3">
              <button
                type="button"
                @click="messageUser = null"
                class="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Annuler
              </button>
              <button
                type="submit"
                class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                :disabled="sendingMessage"
              >
                <icon v-if="sendingMessage" name="Loader" class="h-4 w-4 animate-spin inline mr-1" />
                {{ sendingMessage ? 'Envoi...' : 'Envoyer' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="userToDelete"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="userToDelete = null"
    >
      <div class="bg-white rounded-xl shadow-lg w-full max-w-md">
        <div class="p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-800">Confirmation de suppression</h3>
            <button
              @click="userToDelete = null"
              class="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <icon name="X" :size="20" class="text-gray-500" />
            </button>
          </div>

          <div class="mb-6">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <icon name="AlertTriangle" class="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p class="font-semibold text-gray-800">Êtes-vous sûr ?</p>
                <p class="text-sm text-gray-600">Cette action est irréversible.</p>
              </div>
            </div>
            
            <div class="bg-red-50 border border-red-200 rounded-lg p-4">
              <p class="text-sm text-gray-700 mb-2">
                Vous êtes sur le point de supprimer l'utilisateur :
              </p>
              <p class="font-medium text-gray-800">{{ userToDelete.name }}</p>
              <p class="text-sm text-gray-600">{{ userToDelete.email }}</p>
            </div>
          </div>

          <div class="flex space-x-3">
            <button
              @click="userToDelete = null"
              class="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Annuler
            </button>
            <button
              @click="deleteUser"
              class="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              :disabled="deletingUser"
            >
              <icon v-if="deletingUser" name="Loader" class="h-4 w-4 animate-spin inline mr-1" />
              {{ deletingUser ? 'Suppression...' : 'Supprimer' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import UserDetailModal from './UserDetailModal.vue'

export default {
  name: 'UsersPage',

  components: {
    UserDetailModal
  },

  data() {
    return {
      searchTerm: '',
      filterRole: '',
      filterStatus: '',
      selectedUser: null,
      messageUser: null,
      userToDelete: null,
      updatingStatus: null,
      updatingRole: null,
      refreshing: false,
      exporting: false,
      deletingUser: false,
      sendingMessage: false,
      
      messageForm: {
        subject: '',
        message: ''
      },
      
      // Données locales enrichies
      localUsers: []
    }
  },

  computed: {
    ...mapState(['admin', 'loading', 'error', 'user']),
    
    users() {
      return this.localUsers.length ? this.localUsers : this.admin.users
    },
    
    currentUserId() {
      return this.user?.id
    },
    
    filteredUsers() {
      return this.users.filter(user => {
        // Recherche
        const searchLower = this.searchTerm.toLowerCase()
        const matchesSearch = 
          (user.name && user.name.toLowerCase().includes(searchLower)) ||
          (user.email && user.email.toLowerCase().includes(searchLower)) ||
          (user.phone && user.phone.includes(this.searchTerm))
        
        // Filtre rôle
        const matchesRole = !this.filterRole || user.role.toString() === this.filterRole
        
        // Filtre statut
        const matchesStatus = !this.filterStatus || user.status.toString() === this.filterStatus
        
        return matchesSearch && matchesRole && matchesStatus
      }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // Trier par date décroissante
    },
    
    roleCounts() {
      const counts = {}
      this.filteredUsers.forEach(user => {
        const role = user.role
        counts[role] = (counts[role] || 0) + 1
      })
      return counts
    }
  },

  async created() {
    await this.fetchUsers()
  },

  methods: {
    ...mapActions(['fetchAdminData']),
    
    async fetchUsers() {
      this.refreshing = true
      try {
        // Charger les données admin
        const data = await this.fetchAdminData()
        
        // Si les utilisateurs sont dans les données admin
        if (data && data.users) {
          this.localUsers = data.users.map(user => ({
            ...user,
            // Calculer les stats si elles ne sont pas déjà présentes
            orders_count: user.orders_count || user.orders?.length || 0,
            total_spent: user.total_spent || 0
          }))
        } else if (this.admin.users) {
          // Utiliser les utilisateurs du store
          this.localUsers = this.admin.users.map(user => ({
            ...user,
            orders_count: user.orders_count || 0,
            total_spent: user.total_spent || 0
          }))
        }
        
        // this.$toast.success(`${this.localUsers.length} utilisateurs chargés`)
      } catch (error) {
        console.error(error,'Erreur lors du chargement des utilisateurs:', error)
        // this.$toast.error('Erreur lors du chargement des utilisateurs')
      } finally {
        this.refreshing = false
      }
    },
    
    formatPrice(price) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XAF',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price)
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return dateString
        return date.toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      } catch (error) {
        return dateString
      }
    },
    
    formatTime(dateString) {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return ''
        return date.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return ''
      }
    },
    
    getUserInitials(user) {
      if (!user.name) return '?'
      return user.name
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2)
    },
    
    getRoleColor(role) {
      switch (parseInt(role)) {
        case 1: return 'bg-purple-100 text-purple-800 border border-purple-200' // Admin
        case 0: return 'bg-blue-100 text-blue-800 border border-blue-200' // Client
        default: return 'bg-gray-100 text-gray-800 border border-gray-200'
      }
    },
    
    getRoleLabel(role) {
      const roleNum = parseInt(role)
      const labels = {
        1: 'Administrateur',
        0: 'Client'
      }
      return labels[roleNum] || `Rôle ${role}`
    },
    
    getStatusColor(status) {
      switch (parseInt(status)) {
        case 1: return 'bg-green-100 text-green-800 border border-green-200' // Actif
        case 0: return 'bg-yellow-100 text-yellow-800 border border-yellow-200' // Inactif
        case 2: return 'bg-red-100 text-red-800 border border-red-200' // Bloqué
        default: return 'bg-gray-100 text-gray-800 border border-gray-200'
      }
    },
    
    getStatusLabel(status) {
      const statusNum = parseInt(status)
      const labels = {
        1: 'Actif',
        0: 'Inactif',
        2: 'Bloqué'
      }
      return labels[statusNum] || `Statut ${status}`
    },
    
    async updateUserStatus(userId, newStatus) {
      this.updatingStatus = userId
      try {
        // Appeler l'API pour mettre à jour le statut
        const response = await this.$axios.put(`/api/users/${userId}/status`, {
          status: parseInt(newStatus)
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        
        // Mettre à jour localement
        const userIndex = this.localUsers.findIndex(u => u.id === userId)
        if (userIndex !== -1) {
          this.localUsers[userIndex].status = parseInt(newStatus)
        }
        
        // Mettre à jour l'utilisateur sélectionné si ouvert
        if (this.selectedUser && this.selectedUser.id === userId) {
          this.selectedUser.status = parseInt(newStatus)
        }
        
        // this.$toast.success('Statut mis à jour avec succès')
      } catch (error) {
        console.error('Erreur lors de la mise à jour du statut:', error)
        // this.$toast.error('Erreur lors de la mise à jour du statut')
      } finally {
        this.updatingStatus = null
      }
    },
    
    async updateUserRole(userId, newRole) {
      this.updatingRole = userId
      try {
        // Appeler l'API pour mettre à jour le rôle
        const response = await this.$axios.put(`/api/users/${userId}/role`, {
          role: parseInt(newRole)
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        
        // Mettre à jour localement
        const userIndex = this.localUsers.findIndex(u => u.id === userId)
        if (userIndex !== -1) {
          this.localUsers[userIndex].role = parseInt(newRole)
        }
        
        // Mettre à jour l'utilisateur sélectionné si ouvert
        if (this.selectedUser && this.selectedUser.id === userId) {
          this.selectedUser.role = parseInt(newRole)
        }
        
        // this.$toast.success('Rôle mis à jour avec succès')
      } catch (error) {
        console.error('Erreur lors de la mise à jour du rôle:', error)
        // this.$toast.error('Erreur lors de la mise à jour du rôle')
      } finally {
        this.updatingRole = null
      }
    },
    
    selectUser(user) {
      this.selectedUser = user
    },
    
    sendMessage(user) {
      this.messageUser = user
      this.messageForm = {
        subject: '',
        message: ''
      }
    },
    
    confirmDelete(user) {
      this.userToDelete = user
    },
    
    handleStatusUpdate({ userId, newStatus }) {
      this.updateUserStatus(userId, newStatus)
    },
    
    handleRoleUpdate({ userId, newRole }) {
      this.updateUserRole(userId, newRole)
    },
    
    handleSearch() {
      // Débounce pour éviter trop de re-rendus
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        // Force update si nécessaire
      }, 300)
    },
    
    handleFilter() {
      // Filtre appliqué immédiatement
    },
    
    clearFilters() {
      this.searchTerm = ''
      this.filterRole = ''
      this.filterStatus = ''
    },
    
    async exportUsers() {
      this.exporting = true
      try {
        // Créer un CSV des utilisateurs filtrés
        const headers = ['ID', 'Nom', 'Email', 'Téléphone', 'Rôle', 'Statut', 'Inscription', 'Commandes', 'Total dépensé']
        const csvData = this.filteredUsers.map(user => [
          user.id,
          user.name,
          user.email,
          user.phone || '',
          this.getRoleLabel(user.role),
          this.getStatusLabel(user.status),
          this.formatDate(user.created_at),
          user.orders_count || 0,
          user.total_spent || 0
        ])
        
        // Créer le contenu CSV
        const csvContent = [
          headers.join(','),
          ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n')
        
        // Télécharger le fichier
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `utilisateurs_${new Date().toISOString().split('T')[0]}.csv`
        link.click()
        
        // this.$toast.success('Export terminé')
      } catch (error) {
        console.error('Erreur lors de l\'export:', error)
        // this.$toast.error('Erreur lors de l\'export')
      } finally {
        this.exporting = false
      }
    },
    
    async sendUserMessage() {
      this.sendingMessage = true
      try {
        // Appeler l'API pour envoyer le message
        await this.$axios.post('/api/users/send-message', {
          user_id: this.messageUser.id,
          subject: this.messageForm.subject,
          message: this.messageForm.message
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        
        // Réinitialiser le formulaire et fermer la modal
        this.messageForm = { subject: '', message: '' }
        this.messageUser = null
        
        // this.$toast.success('Message envoyé avec succès')
      } catch (error) {
        console.error('Erreur lors de l\'envoi du message:', error)
        // this.$toast.error('Erreur lors de l\'envoi du message')
      } finally {
        this.sendingMessage = false
      }
    },
    
    async deleteUser() {
      this.deletingUser = true
      try {
        // Appeler l'API pour supprimer l'utilisateur
        await this.$axios.delete(`/api/users/${this.userToDelete.id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
        
        // Supprimer localement
        this.localUsers = this.localUsers.filter(u => u.id !== this.userToDelete.id)
        
        // Fermer les modales
        if (this.selectedUser && this.selectedUser.id === this.userToDelete.id) {
          this.selectedUser = null
        }
        this.userToDelete = null
        
        // this.$toast.success('Utilisateur supprimé avec succès')
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        // this.$toast.error('Erreur lors de la suppression de l\'utilisateur')
      } finally {
        this.deletingUser = false
      }
    }
  }
}
</script>

<style scoped>
/* Styles spécifiques au composant */
input, select, textarea {
  transition: all 0.2s ease;
}

input:focus, select:focus, textarea:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

/* Animation pour les modales */
.fixed {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Animation pour les lignes du tableau */
tr {
  transition: background-color 0.2s ease;
}

/* Style pour les boutons */
button:not(:disabled) {
  transition: all 0.2s ease;
}

button:not(:disabled):hover {
  transform: translateY(-1px);
}

/* Style pour les avatars */
.w-10, .w-12 {
  transition: transform 0.2s ease;
}

.w-10:hover, .w-12:hover {
  transform: scale(1.05);
}

/* Responsive */
@media (max-width: 768px) {
  .flex-col {
    flex-direction: column;
  }
  
  .space-x-2 > * + * {
    margin-left: 0;
    margin-top: 0.5rem;
  }
  
  .hidden {
    display: none;
  }
}
</style>